import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import Alert from '../common/Alert';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import '../../styles/bulk-upload.css';

const BulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    processFile(droppedFile);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  const processFile = (file) => {
    if (!file) return;

    const extension = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(extension)) {
      setErrors([{ message: 'Invalid file type. Please upload CSV or Excel file.' }]);
      return;
    }

    setFile(file);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Get headers and first 5 rows for preview
      const headers = json[0];
      const rows = json.slice(1, 6);
      setPreview({ headers, rows });
    };
    reader.readAsBinaryString(file);
  };

  const downloadTemplate = () => {
    const template = [
      ['product_name', 'sku', 'barcode', 'category', 'cost_price', 'selling_price', 'unit', 'current_stock', 'min_stock_level'],
      ['Sample Product', 'SKU001', '123456789012', 'Electronics', '500', '750', 'piece', '100', '10']
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(template);
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'product_upload_template.xlsx');
  };

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      // Convert any uploaded file (xlsx, xls, csv) into standard CSV blob
      const csvBlob = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const csvText = XLSX.utils.sheet_to_csv(worksheet);
            resolve(new Blob([csvText], { type: 'text/csv' }));
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsBinaryString(file);
      });

      const formData = new FormData();
      formData.append('file', csvBlob, 'upload.csv');

      const response = await api.post('/products.php/bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        setUploadComplete(true);
        setTimeout(() => {
          navigate('/products');
        }, 2000);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      const data = error.response?.data || {};
      const displayErrors = [];
      
      if (data.error) {
        displayErrors.push({ message: data.error });
      }
      
      if (data.errors && Array.isArray(data.errors)) {
        data.errors.forEach(err => displayErrors.push({ message: err }));
      }
      
      if (displayErrors.length === 0) {
        displayErrors.push({ message: 'Upload failed' });
      }
      
      setErrors(displayErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bulk-upload">
      <Card>
        <div className="upload-header">
          <h2>Bulk Product Upload</h2>
          <Button variant="secondary" onClick={downloadTemplate} icon="📥">
            Download Template
          </Button>
        </div>

        {!uploadComplete ? (
          <>
            <div 
              className={`upload-area ${file ? 'has-file' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
            >
              <input
                type="file"
                id="file-upload"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="file-input"
              />
              <label htmlFor="file-upload" className="file-label">
                <span className="upload-icon">📁</span>
                {file ? (
                  <div className="file-info">
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                ) : (
                  <>
                    <p className="upload-text">Drag & drop file here or click to browse</p>
                    <p className="upload-hint">Supports CSV, Excel files (max 10MB)</p>
                  </>
                )}
              </label>
            </div>

            {preview.headers && (
              <div className="preview-section">
                <h3>Preview (First 5 rows)</h3>
                <div className="preview-table">
                  <table>
                    <thead>
                      <tr>
                        {preview.headers.map((header, i) => (
                          <th key={i}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {errors.length > 0 && (
              <div className="errors-section">
                <h3>Errors</h3>
                {errors.map((error, i) => (
                  <Alert key={i} type="error" message={error.message} />
                ))}
              </div>
            )}

            {loading && (
              <div className="progress-section">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="progress-text">{uploadProgress}% Uploaded</p>
              </div>
            )}

            <div className="upload-actions">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/products')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpload}
                disabled={!file || loading}
                loading={loading}
              >
                Upload Products
              </Button>
            </div>

            <div className="upload-instructions">
              <h4>Instructions:</h4>
              <ul>
                <li>Download the template file and fill in your product data</li>
                <li>Product name and selling price are required fields</li>
                <li>SKU and barcode should be unique if provided</li>
                <li>Upload the completed file using the form above</li>
                <li>Maximum file size: 10MB</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="upload-success">
            <div className="success-icon">✅</div>
            <h3>Upload Successful!</h3>
            <p>Your products have been uploaded successfully.</p>
            <p>Redirecting to product list...</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BulkUpload;